/**
 * The typed link to the REAL backend router.
 *
 * ┌─────────────────────────────────────────────────────────────────────────────────────────┐
 * │ WHY THIS FILE EXISTS NEXT TO ./client.ts, AND WHY THAT IS TEMPORARY                     │
 * │                                                                                         │
 * │ `./client.ts` is parameterised on `./router-type.ts`, which still re-exports the        │
 * │ hand-written placeholder in `./contract.ts` (`konto.saldo` + `verrechnungskonto.status`,│
 * │ neither of which exists on the real router — the real one calls it `posteingang`).      │
 * │ Swapping `router-type.ts` over therefore breaks `./queries.ts` in the same commit, and  │
 * │ that file belongs to the Posteingang/Import work that is in flight.                     │
 * │                                                                                         │
 * │ So this module takes the correct dependency — `@bankingsolution/backend/types`, a TYPE, │
 * │ through the package export — without touching the placeholder. The two clients hit the  │
 * │ same URL with the same auth header and coexist.                                         │
 * │                                                                                         │
 * │ TO FINISH THE MIGRATION (one commit, no behaviour change):                              │
 * │   1. port ./queries.ts onto `konto.saldoAlle` / `posteingang.list`                      │
 * │   2. point ./router-type.ts at '@bankingsolution/backend/types'                         │
 * │   3. delete ./contract.ts and this file; `trpcClient` is then the only client           │
 * └─────────────────────────────────────────────────────────────────────────────────────────┘
 *
 * 🔴 What this is NOT: a path alias into ../backend/src. That is the ecorize defect
 * (CLAUDE.md §5) and it welds two independently deployable packages into one build. Every
 * import below is `import type` from the package's `./types` entrypoint, which is a module
 * containing no runtime values at all — `pg` cannot reach a browser bundle through it.
 */
import { createTRPCClient, httpBatchLink } from '@trpc/client'
import type { inferRouterInputs, inferRouterOutputs } from '@trpc/server'

import type { AppRouter } from '@bankingsolution/backend/types'
import { acquireAccessToken } from '~/lib/auth/msal.ts'
import { env } from '~/lib/env.ts'

export const backendClient = createTRPCClient<AppRouter>({
  links: [
    httpBatchLink({
      url: `${env.VITE_API_URL}/trpc`,
      headers: async () => {
        const token = await acquireAccessToken()
        return token ? { authorization: `Bearer ${token}` } : {}
      },
    }),
  ],
})

/**
 * Inputs are the schemas' INPUT side (before `zId`'s `.transform(BigInt)`), so an id is a
 * string here — which is also what it is on the wire and in a URL search param. Money likewise
 * never becomes a `number` on either side (backend/src/trpc/schemas.ts, "the two wire rules").
 */
export type BackendInputs = inferRouterInputs<AppRouter>
export type BackendOutputs = inferRouterOutputs<AppRouter>
